import { Box, Typography, Button, Paper, Stack } from '@mui/material';
import { COLORS } from '../utils/constants';
import {
  Inventory2 as BulkIcon,
  LocalOffer as OfferIcon,
  TrendingUp as GrowthIcon,
} from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import React from 'react';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Bulk Booking Promotion Component
 * Shows benefits of bulk booking feature for all users (business, individual, logged-in, guest)
 */
export default function BulkBookingPromo() {
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        border: `1px solid #fbbf24`,
        mt: 2,
        overflow: 'hidden',
        width: '100%',
        transition: 'all 0.3s ease',
        position: 'relative',
        zIndex: 1, // Ensure it doesn't overlap with Price Summary
        '&:hover': {
          boxShadow: '0 8px 24px rgba(245, 158, 11, 0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            background: '#fbbf24',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BulkIcon sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: '#92400e', fontSize: '0.95rem' }}
          >
            📦 {t('estimate.bulk.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#b45309', fontSize: '0.7rem' }}>
            {t('estimate.bulk.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Features Stack */}
      <Stack spacing={1.8}>
        {/* Feature 1: Save Time */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box
            sx={{
              minWidth: 24,
              height: 24,
              borderRadius: '50%',
              background: '#fbbf24',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.2,
            }}
          >
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#fff' }}>✓</Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#78350f',
                display: 'block',
                mb: 0.25,
              }}
            >
              Save Time
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#92400e', lineHeight: 1.5, fontSize: '0.7rem' }}
            >
              {t('estimate.bulk.saveTimeDesc')}
            </Typography>
          </Box>
        </Box>

        {/* Feature 2: Better Rates */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box
            sx={{
              minWidth: 24,
              height: 24,
              borderRadius: '50%',
              background: '#fbbf24',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.2,
            }}
          >
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#fff' }}>✓</Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#78350f',
                display: 'block',
                mb: 0.25,
              }}
            >
              {t('estimate.bulk.getDiscounts')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#92400e', lineHeight: 1.5, fontSize: '0.7rem' }}
            >
              {t('estimate.bulk.getDiscountsDesc')}
            </Typography>
          </Box>
        </Box>

        {/* Feature 3: Contracts */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box
            sx={{
              minWidth: 24,
              height: 24,
              borderRadius: '50%',
              background: '#fbbf24',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.2,
            }}
          >
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#fff' }}>✓</Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#78350f',
                display: 'block',
                mb: 0.25,
              }}
            >
              {t('estimate.bulk.businessContracts')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#92400e', lineHeight: 1.5, fontSize: '0.7rem' }}
            >
              {t('estimate.bulk.businessContractsDesc')}
            </Typography>
          </Box>
        </Box>
      </Stack>

      {/* CTA Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={() => router.push('/bulk-booking')}
        startIcon={<BulkIcon />}
        sx={{
          mt: 2.5,
          background: '#f59e0b',
          color: '#fff',
          fontWeight: 700,
          textTransform: 'none',
          fontSize: '0.9rem',
          py: 1.2,
          borderRadius: 1.5,
          '&:hover': {
            background: '#d97706',
            boxShadow: '0 4px 12px rgba(217, 119, 6, 0.4)',
          },
        }}
      >
        {t('estimate.bulk.tryNow')}
      </Button>

      {/* Sub-text */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          mt: 1.5,
          color: '#b45309',
          fontSize: '0.65rem',
          fontWeight: 500,
        }}
      >
        {t('estimate.bulk.availableToAll')}
      </Typography>
    </Paper>
  );
}
